import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ExternalLink, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { returnCountdown } from "@/lib/format";

const LEAGUES = ["NHL", "NBA", "NFL", "MLB"];

const BLURB = {
  NHL: "Every NHL team's injured and out players — official status designations, straight from ESPN, each with a source link.",
  NBA: "Every NBA team's injury & availability report — Out, Doubtful, Day-To-Day and Game-Time Decisions, source-cited.",
  NFL: "Every NFL team's injury designations — Out, Doubtful, Questionable and IR, with injury type and expected return. Source-cited.",
  MLB: "Every MLB team's injured list and status report — official designations with injury type and expected return, source-cited.",
};

function sevColor(sev) {
  switch (sev) {
    case "Ruled Out":
    case "Suspended": return "#F87171";
    case "Doubtful": return "#FB923C";
    case "Game-Time Decision": return "#FBBF24";
    case "Questionable": return "#FFB020";
    default: return "#94A3B8";
  }
}

function PlayerRow({ p }) {
  const color = sevColor(p.severity);
  const rc = returnCountdown(p.return_date);
  const inner = (
    <div className="flex items-start gap-3 py-2.5">
      {p.headshot
        ? <img src={p.headshot} alt="" className="h-9 w-9 rounded-full object-cover bg-[#151B2B] shrink-0" />
        : <div className="h-9 w-9 rounded-full bg-[#151B2B] shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-100 truncate">{p.name}</span>
          {p.position && <span className="text-[10px] font-mono text-slate-500">{p.position}</span>}
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded"
            style={{ color, background: `${color}1a` }}>
            {p.status}
          </span>
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {p.injury && <span className="text-slate-300">{p.injury}</span>}
          {rc && (
            <span className="flex items-center gap-1 font-mono"
              style={{ color: rc.soon ? "#FFB020" : "#64748B" }}
              data-testid={`status-return-${p.player_id || p.name}`}>
              <CalendarClock size={11} /> {rc.label}
            </span>
          )}
        </div>
        {p.comment && <p className="text-[11px] text-slate-500 mt-1 leading-snug line-clamp-2">{p.comment}</p>}
        <div className="flex items-center gap-3 mt-1">
          {p.player_id && (
            <Link to={`/players/${p.player_id}`} className="text-[11px] font-mono text-[#38BDF8] hover:underline">
              View player
            </Link>
          )}
          {p.source_url && (
            <a href={p.source_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] font-mono text-slate-500 hover:text-slate-300 flex items-center gap-1">
              ESPN <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
  return <div data-testid={`status-player-${p.player_id || p.name}`} className="border-b border-[#1A2133] last:border-0">{inner}</div>;
}

function TeamCard({ group }) {
  const t = group.team || {};
  return (
    <div data-testid={`status-team-${t.abbr || t.name}`}
      className="rounded-xl bg-[#0F1420] border border-[#232D42] p-4">
      <div className="flex items-center gap-2.5 mb-2">
        {t.logo && <img src={t.logo} alt="" className="h-8 w-8 object-contain shrink-0" />}
        <div className="min-w-0">
          <div className="text-sm font-heading font-bold text-slate-100 truncate">{t.name}</div>
          <div className="text-[10px] font-mono text-slate-500">{group.count} on report</div>
        </div>
      </div>
      <div className="divide-y divide-[#1A2133]">
        {group.players.map((p, i) => <PlayerRow key={p.player_id || p.name || i} p={p} />)}
      </div>
    </div>
  );
}

export default function StatusReport() {
  const [league, setLeague] = useState("NHL");
  const [data, setData] = useState({ teams: [], count: 0, team_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/status-report?league=${league}`)
      .then((r) => setData(r.data || { teams: [] }))
      .catch(() => setData({ teams: [] }))
      .finally(() => setLoading(false));
  }, [league]);

  return (
    <div data-testid="status-report-page" className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 mb-1 flex items-center gap-2">
        <Activity size={22} className="text-[#00E5A3]" /> Injury &amp; Status Report
        <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">LIVE</Badge>
      </h1>
      <p className="text-sm text-slate-400 mb-4 max-w-2xl">{BLURB[league]}</p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {LEAGUES.map((lg) => (
          <button key={lg} data-testid={`status-league-${lg}`} onClick={() => setLeague(lg)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${league === lg ? "bg-[#38BDF8] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {lg}
          </button>
        ))}
        {!loading && data.count > 0 && (
          <span className="text-[11px] font-mono text-slate-500 ml-1">
            {data.count} players · {data.team_count} teams
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading report…</div>
      ) : (data.teams || []).length === 0 ? (
        <div data-testid="status-empty" className="text-center py-16 text-slate-500 text-sm">
          No active {league} injury designations right now — the board fills as teams post their reports.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.teams.map((g, i) => <TeamCard key={(g.team?.abbr || g.team?.name || i)} group={g} />)}
        </div>
      )}

      <p className="text-[11px] text-slate-600 mt-6 max-w-2xl leading-relaxed">
        Source: ESPN official injury designations (Out / Doubtful / Questionable / Day-To-Day / IR),
        updated continuously. These are game-status designations, not a Wednesday–Friday practice-participation
        grid. Research only — not betting advice.
      </p>
    </div>
  );
}
